import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import api from '../../services/api.js';
import { BRAND } from '../../brand.js';
import { BrandLockup, BrandStripe } from '../../components/brand/BrandMark.jsx';
import './auth-form.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Value props shown on the branding panel. Kept to the capabilities the app
// actually ships (they mirror the primary navigation).
const MARKETING_POINTS = [
  {
    icon: 'fa fa-map-marked-alt',
    title: 'Capture in the field',
    body: 'Pin properties on the map and turn chat threads into structured listings.',
  },
  {
    icon: 'fa fa-stamp',
    title: 'Verify against the registry',
    body: 'Adjudicate title checks and keep an auditable record of every decision.',
  },
  {
    icon: 'fa fa-sliders-h',
    title: 'Match and deliver',
    body: 'Score sites against mandates and send developers a ready-made pack.',
  },
];

const COPY = {
  title: 'Sign in',
  subtitle: `Sign in to continue to ${BRAND.name}.`,
  submit: 'Sign in',
  busy: 'Signing in…',
};

function validate(values) {
  const errors = {};

  if (!values.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_RE.test(values.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }

  if (!values.password) {
    errors.password = 'Password is required.';
  }

  return errors;
}

/**
 * The sign-in screen. There is no register counterpart: the app is internal, and
 * accounts are created by an Admin in User Management. See auth.routes.js for
 * why self-service sign-up is not merely hidden here but gone from the API.
 */
function AuthForm() {
  const copy = COPY;
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [values, setValues] = useState({ email: '', password: '', jurisdiction: '' });
  const [markets, setMarkets] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /*
    Which markets this deployment serves. Fetched unauthenticated, because the
    selector has to render before anyone has a token.

    A failure here must NOT block sign-in: the deployment still has a default
    market, and the server applies it when the field is absent. So a failed
    call means "no choice to offer", never "no way in".
  */
  useEffect(() => {
    let cancelled = false;

    api
      .jurisdictions()
      .then((data) => {
        if (cancelled) return;
        setMarkets(data.jurisdictions ?? []);
        setValues((prev) => ({ ...prev, jurisdiction: data.default ?? '' }));
      })
      .catch(() => {
        if (!cancelled) setMarkets([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Where the guards wanted to send us before bouncing here.
  const redirectTo = location.state?.from?.pathname || '/';

  // Already signed in? Nothing to do on an auth screen.
  if (!loading && isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const setField = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setFormError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setFormError('');

    try {
      await login(values.email.trim(), values.password, values.jurisdiction || undefined);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setFormError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="row g-0 auth-row">
          {/* Marketing / branding panel — LEFT per the OS-standard design. */}
          <div className="col-lg-6 auth-brand-panel d-none d-lg-flex flex-column justify-content-between p-5">
            <div>
              <div className="auth-brand-mark">
                <BrandLockup size={40} />
              </div>
              <div className="auth-brand-tagline mt-2">{BRAND.tagline}</div>
            </div>

            <div>
              <h2 className="auth-brand-headline mb-3">
                Source, verify and match land in {BRAND.market} — end to end.
              </h2>
              <p className="auth-brand-copy mb-4">
                Capture sites in the field, adjudicate registry checks, score matches and deliver
                packs to developers, all from one workspace.
              </p>

              <ul className="auth-brand-list list-unstyled mb-0">
                {MARKETING_POINTS.map((point) => (
                  <li className="d-flex align-items-start mb-3" key={point.title}>
                    <span className="auth-brand-icon me-3">
                      <i className={point.icon} aria-hidden="true" />
                    </span>
                    <span>
                      <span className="d-block fw-semibold">{point.title}</span>
                      <span className="auth-brand-copy small">{point.body}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="auth-powered-by mb-1">Powered by Xamun</p>
              <p className="auth-brand-copy small mb-0">
                &copy; {new Date().getFullYear()} {BRAND.name}
              </p>
            </div>
            <BrandStripe />
          </div>

          {/* Auth form — RIGHT. */}
          <div className="col-12 col-lg-6 auth-form-col">
            <div className="auth-form-inner p-4 p-sm-5">
              {/* Brand lockup for narrow screens, where the panel is hidden. */}
              <div className="auth-brand d-lg-none text-center mb-3">
                <BrandLockup size={32} />
              </div>

              <div className="mb-4">
                <h1 className="h4 mb-1">{copy.title}</h1>
                <p className="text-muted small mb-0">{copy.subtitle}</p>
              </div>

              {formError && (
                <div className="alert alert-danger d-flex align-items-start" role="alert">
                  <i className="fa fa-circle-exclamation me-2 mt-1" aria-hidden="true" />
                  <span>{formError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/*
                  Only rendered when there is a choice to make. A single-market
                  deployment has no selector AT ALL rather than a disabled one —
                  which is what makes narrowing a client deploy a config change
                  (JURISDICTIONS=AE) instead of a code change.
                */}
                {markets && markets.length > 1 && (
                  <div className="mb-3">
                    <label className="form-label" htmlFor="auth-jurisdiction">
                      Market
                    </label>
                    <select
                      id="auth-jurisdiction"
                      className="form-select"
                      value={values.jurisdiction}
                      onChange={setField('jurisdiction')}
                    >
                      {markets.map((market) => (
                        <option key={market.code} value={market.code}>
                          {market.name}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Switching market later means signing out and back in.
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label" htmlFor="auth-email">
                    Email
                  </label>
                  <input
                    id="auth-email"
                    type="email"
                    className={`form-control${errors.email ? ' is-invalid' : ''}`}
                    value={values.email}
                    onChange={setField('email')}
                    autoComplete="email"
                    aria-invalid={!!errors.email}
                    placeholder="you@company.com"
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="auth-password">
                    Password
                  </label>
                  <div className="input-group">
                    <input
                      id="auth-password"
                      type={showPassword ? 'text' : 'password'}
                      className={`form-control${errors.password ? ' is-invalid' : ''}`}
                      value={values.password}
                      onChange={setField('password')}
                      autoComplete="current-password"
                      aria-invalid={!!errors.password}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      <i
                        className={showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'}
                        aria-hidden="true"
                      />
                    </button>
                    {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100 py-2 fw-semibold"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                      {copy.busy}
                    </>
                  ) : (
                    copy.submit
                  )}
                </button>
              </form>

              {import.meta.env.DEV && (
                <div className="auth-demo mt-4 pt-3 border-top">
                  <p className="text-muted small mb-1 fw-semibold">Demo accounts</p>
                  <ul className="text-muted small mb-0 ps-3">
                    <li>admin@example.com / password123</li>
                    <li>analyst@example.com / password123</li>
                    <li>principal@example.com / password123</li>
                  </ul>
                </div>
              )}

              {/*
                The guide is public on purpose — being unable to get in is one
                of the moments you most need it. The shell's footer used to be
                how you reached it from here; these screens render outside the
                shell now, so the link has to live on the page itself.
              */}
              <p className="text-center mt-4 mb-0">
                <Link className="text-muted small text-decoration-none" to="/help">
                  <i className="fa fa-circle-question me-1" aria-hidden="true" />
                  Help &amp; user guide
                </Link>
              </p>

              {/* The branding panel carries this on lg+, but it is hidden below
                  that breakpoint — so repeat it here rather than lose it. */}
              <p className="auth-powered-by auth-powered-by-sm d-lg-none text-center mt-3 mb-0">
                Powered by Xamun
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  return <AuthForm />;
}

export default LoginPage;
